import type { Branded } from '#layers/base/types/utils';

const generatedItemMarker = 'gen\0';

export type GeneratedKey = Branded<string, 'GeneratedKey'>;

/**
 * Checks if a given key is a generated key by verifying it starts with the generated item marker.
 *
 * @param key - The key to check, can be a string, number, null, or undefined
 * @returns True if the key is a string that starts with the generated item marker, false otherwise
 *
 * @example
 * ```typescript
 * isGeneratedKey("gen\0abc123"); // returns true
 * isGeneratedKey("regular-key"); // returns false
 * isGeneratedKey(123); // returns false
 * isGeneratedKey(null); // returns false
 * ```
 */
export function isGeneratedKey(key: string | number | null | undefined): key is GeneratedKey {
	return typeof key === 'string' && key.startsWith(generatedItemMarker);
}

/**
 * Creates a generated key by prefixing the provided key with the generated item marker.
 *
 * @param key - The base key to be prefixed with the generated item marker
 * @returns A string with the generated item marker prefix
 *
 * @example
 * ```typescript
 * getGeneratedKey("abc123"); // returns "gen\0abc123"
 * getGeneratedKey("temp-item"); // returns "gen\0temp-item"
 * ```
 */
export function getGeneratedKey(key: string): GeneratedKey {
	return `${generatedItemMarker}${key}` as GeneratedKey;
}

export function stripGeneratedKey(key: string | GeneratedKey): string {
	return key.replace(generatedItemMarker, '');
}
